import type { MetricSeries } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { getMetricSeries } from '../api/metrics.js';
import { formatBytes, formatHorizon, formatPercent, formatRate } from '../lib/format.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';
import { MetricChart } from './MetricChart.js';

/**
 * Disk, on a server's page (knowledge doc sections 8.3 and 15).
 *
 * Two questions, two shapes, because they are different questions. **Is it filling
 * up?** is change over time, so it is a line per mount with section 8.3's growth
 * rate and "full in N days" beside it. **What is taking the room?** is magnitude at
 * one moment, so it is a ranked list of folders — twenty lines on a chart would be
 * unreadable, and the ranking is the whole answer anyway.
 */

/** A percentage, so "full" is a fixed number rather than one per server. */
const FULL_PCT = 100;

const RANGES = [
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
] as const;

export function DiskCard({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const [hours, setHours] = useState('24');

  const since = (): string => new Date(Date.now() - Number(hours) * 3_600_000).toISOString();

  const usage = useQuery({
    queryKey: ['metrics', serverId, 'disk_used_pct', hours],
    queryFn: () =>
      getMetricSeries({
        serverId,
        name: 'disk_used_pct',
        from: since(),
        trendTarget: FULL_PCT,
      }),
  });

  const folders = useQuery({
    queryKey: ['metrics', serverId, 'disk_folder_bytes', hours],
    queryFn: () => getMetricSeries({ serverId, name: 'disk_folder_bytes', from: since() }),
  });

  return (
    <Card
      title="Disk"
      description="Every real mount, and what is growing."
      aside={
        // One control, above the content it scopes, rather than one per chart.
        <SelectField
          label="Range"
          value={hours}
          onChange={(event) => {
            setHours(event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      }
    >
      {usage.isError && (
        <Alert tone="danger" title="Could not load the disk history">
          Try again in a moment.
        </Alert>
      )}

      {usage.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading disk usage…
        </p>
      )}

      {usage.data !== undefined && (
        <>
          <MetricChart
            title="Disk used, per mount"
            series={usage.data.series}
            from={usage.data.from}
            to={usage.data.to}
            bucketSeconds={usage.data.bucketSeconds}
            format={(value) => formatPercent(value)}
            seriesLabel={(series) => series.labels['mount'] ?? 'unknown mount'}
            yMin={0}
            yMax={FULL_PCT}
            timezone={timezone}
          />
          <Forecasts series={usage.data.series} />
        </>
      )}

      {folders.data !== undefined && folders.data.series.length > 0 && (
        <LargestFolders series={folders.data.series} />
      )}
    </Card>
  );
}

/**
 * Section 8.3: "The API calculates growth rate and 'full in N days' estimates."
 *
 * Only the mounts that are actually heading somewhere are named. A list that says
 * "not growing" five times is a list nobody reads, and the one line that matters
 * has to be found in it.
 */
function Forecasts({ series }: { series: MetricSeries[] }): ReactNode {
  const filling = series
    .filter((line) => line.trend !== null && line.trend.reachesTargetInDays !== null)
    .sort((a, b) => (a.trend?.reachesTargetInDays ?? 0) - (b.trend?.reachesTargetInDays ?? 0));

  if (filling.length === 0) {
    return (
      <p className="muted text-sm">
        Nothing is on course to fill up over this range. The estimate needs at least three readings
        and a mount that is actually growing.
      </p>
    );
  }

  return (
    <ul className="forecasts">
      {filling.map((line) => {
        const days = line.trend?.reachesTargetInDays ?? 0;
        return (
          <li key={line.labels['mount'] ?? 'unknown'} className={days < 7 ? 'urgent' : undefined}>
            <strong>{line.labels['mount'] ?? 'unknown mount'}</strong> is full in{' '}
            <strong>{formatHorizon(days)}</strong>
            <span className="cell-sub">
              {formatRate(line.trend?.changePerDay ?? 0, (value) => formatPercent(value, 2))},
              fitted over {String(line.trend?.samples ?? 0)} readings
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The largest folders (section 8.3: `du -x --max-depth=2`, top twenty).
 *
 * A ranked bar list rather than a chart: the comparison is between folders at one
 * moment, and a single hue carries magnitude without spending eight categorical
 * colours on a question that has no categories.
 */
function LargestFolders({ series }: { series: MetricSeries[] }): ReactNode {
  const latest = series
    .map((line) => ({
      path: line.labels['path'] ?? 'unknown',
      mount: line.labels['mount'] ?? '',
      bytes: line.points[line.points.length - 1]?.avg ?? 0,
    }))
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 10);

  const largest = latest[0]?.bytes ?? 1;

  return (
    <>
      <h3 className="subhead">Largest folders</h3>
      <p className="muted text-xs">Scanned once an hour at low priority, two levels deep.</p>
      <table className="table folder-table">
        <caption className="visually-hidden">The ten largest folders, by size</caption>
        <thead>
          <tr>
            <th scope="col">Folder</th>
            <th scope="col">Size</th>
          </tr>
        </thead>
        <tbody>
          {latest.map((folder) => (
            <tr key={`${folder.mount}:${folder.path}`}>
              <th scope="row">
                <code>{folder.path}</code>
                {folder.mount !== '' && <span className="cell-sub">on {folder.mount}</span>}
              </th>
              <td>
                <span className="bar-cell">
                  <span
                    className="bar"
                    style={{ width: `${String(Math.max(2, (folder.bytes / largest) * 100))}%` }}
                    aria-hidden="true"
                  />
                  <span className="bar-value">{formatBytes(folder.bytes)}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
